import React from 'react';
import { graphql } from 'react-relay';

import BaseLayout from '@zego/layouts/BaseLayout';
import withData from '@zego/lib/withData';
import { infoColor, unit } from '@zego/theme';

import AppStoreLink from '@zego/components/AppStoreLink';
import H1 from '@zego/components/atoms/H1';
import H2 from '@zego/components/atoms/H2';
import RegularText from '@zego/components/atoms/RegularText';
import Spacer from '@zego/components/atoms/Spacer';
import ContentContainer from '@zego/components/ContentContainer';
import GooglePlayLink from '@zego/components/GooglePlayLink';
import HeroButton from '@zego/components/HeroButton';
import ProductCustomerService from '@zego/components/ProductCustomerService';
import {
  LeftFeature,
  RightFeature,
} from '@zego/components/ProductFeatureBlock';
import ProductHero from '@zego/components/ProductHero';
import ProductPageAppDownload from '@zego/components/ProductPageAppDownload';
import ProductTheme from '@zego/components/ProductTheme';
import SectionDisplay from '@zego/components/SectionDisplay';
import SubItemsSection from '@zego/components/SubItemsSection';

import image3 from '../gb/CarGb/static/CarDesignedForYou.jpg';
import image2 from '../gb/CarGb/static/CarFullMobile.jpg';
import image1 from '../gb/CarGb/static/CarMoreFlexibility.jpg';
import heroImage from '../gb/CarGb/static/hero-car.jpg';

const HOW_IT_WORKS_CONTENT = {
  title: 'How It Works',
  itemsTitleAlign: 'left',
  itemsDescriptionAlign: 'left',
  descriptionAlign: 'left',
  items: [
    {
      title: 'Download the app',
      numberHeader: '.01',
      description: (
        <RegularText>
          Get started by downloading the Zego app, you can find us on{' '}
          <AppStoreLink color={infoColor(5)}>iTunes</AppStoreLink> and{' '}
          <GooglePlayLink color={infoColor(5)}>Google Play</GooglePlayLink>.
        </RegularText>
      ),
    },
    {
      title: 'Get a quote',
      numberHeader: '.02',
      description:
        'Answer a few questions on the app, and we’ll give you a 30 day car insurance quote in minutes.',
    },
    {
      title: 'Get covered',
      numberHeader: '.03',
      description:
        'Simply add your payment details and we’ll issue your 30 day policy instantly so you can get out on the road and start earning.',
    },
  ],
};

class CarIe30DayPage extends React.PureComponent {
  render() {
    const { viewer } = this.props;

    return (
      <BaseLayout
        viewer={viewer}
        title="Zego 30 day car insurance | Courier work | Fast food delivery"
        description="Get 30 day car insurance!"
      >
        <ProductTheme product={viewer.product}>
          <React.Fragment>
            <ProductHero image={heroImage}>
              <ProductHero.Header>
                30 day car insurance for delivery drivers.
              </ProductHero.Header>
              <ProductHero.SubHeader>
                Get one simple policy that covers your delivery work and your
                free time with Zego.
              </ProductHero.SubHeader>
              <HeroButton label="Get the app" selector="#download-app" />
            </ProductHero>
            <Spacer height={unit(12)} />
            <SubItemsSection {...HOW_IT_WORKS_CONTENT} />
            <Spacer height={unit(12)} mobileHeight={unit(6)} />
            <SectionDisplay>
              <ContentContainer>
                <H1 css={{ maxWidth: 400 }}>
                  30 day insurance designed with delivery drivers in mind
                </H1>
              </ContentContainer>
            </SectionDisplay>
            <LeftFeature image={image1}>
              <H2>Cover for your work and your free time</H2>
              <RegularText css={{ paddingTop: unit(3) }}>
                We’ve created our 30 day car insurance specifically for
                delivery drivers like you. Simple and convenient, it combines
                Social, Domestic and Pleasure insurance with Hire and Reward
                insurance. That means you get cover for your work and your free
                time - without having to worry about separate policies.
              </RegularText>
            </LeftFeature>
            <RightFeature image={image2}>
              <H2>Perfect for the busy months</H2>
              <RegularText css={{ paddingTop: unit(3) }}>
                It’s ideal if you’re working a lot on a temporary basis, or if
                you’re a full-time delivery driver who doesn’t want to commit
                to an annual policy.
              </RegularText>
            </RightFeature>
            <LeftFeature image={image3} variant="B">
              <H2>Quotes in minutes</H2>
              <RegularText css={{ paddingTop: unit(3) }}>
                To get a quote for 30 day car insurance, all you have to do is
                download the Zego app. You can find us on Google Play and the
                App Store. We’ll have you set up with a quote in minutes.
              </RegularText>
            </LeftFeature>

            <ProductPageAppDownload />
            <ProductCustomerService viewer={viewer} />
          </React.Fragment>
        </ProductTheme>
      </BaseLayout>
    );
  }
}

export default withData(CarIe30DayPage, {
  query: graphql`
    query CarIe30DayPageQuery($country: String!, $code: String!) {
      viewer {
        ...ProductCustomerService_viewer @arguments(country: $country)
        product: websiteMarketingProductByCodeAndCountryCode(
          code: $code
          countryCode: $country
        ) {
          ...ProductTheme_product
        }
        ...BaseLayout_viewer @arguments(country: $country)
      }
    }
  `,
  getVariables: () => {
    return {
      country: 'ie',
      code: 'CAR',
    };
  },
});
